import React, { useEffect, useState } from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';

import { Button, Checkbox, Input, Slider, cn } from '@design-system';

interface CustomAmountInputProps {
  name: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  updateClient?: (value: number) => void;
  formatValue?: (value: number) => string | number;
  testId?: string;
}

function CustomAmountInput({
  name,
  value,
  min,
  max,
  onChange,
  updateClient,
  formatValue,
  testId,
}: CustomAmountInputProps) {
  const [localValue, setLocalValue] = useState<string>(String(value || ''));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused && typeof value === 'number' && !isNaN(value)) {
      setLocalValue(String(value));
    }
  }, [value, isFocused]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const inputValue = event.target.value;
    setLocalValue(inputValue);
    if (inputValue !== '' && !isNaN(parseFloat(inputValue))) {
      const numValue = parseFloat(inputValue);
      if (numValue >= min / 1000) {
        const clampedValue = Math.max(min, Math.min(max, numValue));
        onChange(clampedValue);
      }
    }
  }

  function handleBlur() {
    setIsFocused(false);
    const parsedValue = localValue === '' ? min : parseFloat(localValue) || min;
    const clampedValue = Math.max(min, Math.min(max, parsedValue));
    setLocalValue(String(clampedValue));
    onChange(clampedValue);
    if (updateClient) {
      updateClient(clampedValue);
    }
  }

  function handleFocus() {
    setIsFocused(true);
  }

  return (
    <div className="flex items-center gap-3">
      <label
        htmlFor={`${name}-custom-input`}
        className="text-foreground text-sm font-medium"
      >
        Custom Amount:
      </label>
      <Input
        id={`${name}-custom-input`}
        type="number"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        className="h-9 w-32"
        placeholder={formatValue ? String(formatValue(min)) : String(min)}
        data-testid={`${testId}-custom-input`}
      />
    </div>
  );
}

function SliderTooltip({
  isHovered,
  value,
  position,
  suffix = '',
}: {
  isHovered: boolean;
  value: number | string;
  position: number;
  isDefault?: boolean;
  suffix?: string;
}) {
  return (
    <div
      className={cn(
        'pointer-events-none absolute z-50 -mt-10 whitespace-nowrap rounded-md bg-black px-3 py-1.5 text-xs text-white opacity-0 transition-opacity',
        {
          'opacity-100': isHovered,
        },
      )}
      style={{
        left: `${position}%`,
        transform: `translateX(-${position}%)`,
      }}
    >
      <span className="font-semibold">
        {value}
        {suffix}
      </span>
    </div>
  );
}

interface CustomSliderProps {
  'name': string;
  'control': Control<any>;
  'min': number;
  'max': number;
  'step'?: number;
  'defaultValue': number;
  'formatValue'?: (value: number) => string | number;
  'updateClient'?: (value: number) => void;
  'labelSuffix'?: string;
  'defaultLabel'?: string;
  'hideResetButton'?: boolean;
  'label'?: string;
  'data-testid'?: string;
  'showCustomInput'?: boolean;
  'customInputLabel'?: string;
  'showMinMax'?: boolean;
  'minMaxSuffix'?: string;
  'headerActions'?: React.ReactNode;
  'onReset'?: () => void;
}

export function CustomSlider({
  name,
  control,
  min,
  max,
  step = 1,
  defaultValue,
  formatValue,
  updateClient,
  labelSuffix = '',
  defaultLabel = 'Default',
  hideResetButton = false,
  label,
  'data-testid': testId,
  showCustomInput = false,
  customInputLabel = 'Enter custom amount',
  showMinMax = false,
  minMaxSuffix = '',
  headerActions,
  onReset,
}: CustomSliderProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [showCustomInputField, setShowCustomInputField] = useState(false);
  const currentValue = useWatch({ control, name });

  useEffect(() => {
    if (currentValue === defaultValue) {
      onReset?.();
    }
  }, [defaultValue, currentValue]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const isDefault = value === defaultValue;

        function handleReset(event: React.MouseEvent) {
          event.stopPropagation();
          event.preventDefault();

          onChange(defaultValue);
          onReset?.();
          if (updateClient) {
            updateClient(defaultValue);
          }
        }

        return (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              {label && <div className="text-primary-dark text-lg font-semibold">{label}</div>}
              <div className="ml-auto flex items-center gap-2">
                {headerActions}
                {!isDefault && !hideResetButton && (
                  <Button
                    onClick={handleReset}
                    variant="outline"
                    type="button"
                    size="sm"
                    className="h-7 px-2 text-xs"
                  >
                    {defaultLabel}
                  </Button>
                )}
              </div>
            </div>
            <div className={showMinMax ? 'flex items-center gap-3' : 'relative'}>
              {showMinMax && (
                <span className="text-sm text-gray-400">
                  {min}
                  {minMaxSuffix}
                </span>
              )}
              <div className="relative flex-1">
                <Slider
                  value={[value]}
                  onValueChange={newValues => {
                    onChange(newValues[0]);
                    if (updateClient) {
                      updateClient(newValues[0]);
                    }
                  }}
                  min={min}
                  max={max}
                  step={step}
                  className="**:data-[slot=slider-thumb]:size-4 **:data-[slot=slider-track]:h-1.5"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  data-testid={testId}
                />

                <SliderTooltip
                  isHovered={isHovered}
                  value={formatValue ? formatValue(value) : value}
                  position={((value - min) / (max - min)) * 100}
                  isDefault={isDefault}
                  suffix={labelSuffix}
                />
              </div>
              {showMinMax && (
                <span className="text-sm text-gray-400">
                  {max}
                  {minMaxSuffix}
                </span>
              )}
            </div>
            {showCustomInput && (
              <>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`${name}-show-custom-amount`}
                    checked={showCustomInputField}
                    onCheckedChange={checked => setShowCustomInputField(checked === true)}
                  />
                  <label
                    htmlFor={`${name}-show-custom-amount`}
                    className="text-muted-foreground cursor-pointer text-sm"
                  >
                    {customInputLabel}
                  </label>
                </div>
                {showCustomInputField && (
                  <CustomAmountInput
                    name={name}
                    value={value}
                    min={min}
                    max={max}
                    onChange={onChange}
                    updateClient={updateClient}
                    formatValue={formatValue}
                    testId={testId}
                  />
                )}
              </>
            )}
          </div>
        );
      }}
    />
  );
}
